import { useCallback, useEffect, useRef, useState } from "react";

// An installed PWA can stay open for days and keep serving the JavaScript it
// loaded on the first launch (the service worker streams HTML from the network,
// but nothing reloads the page itself). Poll the shell and reload once the
// published bundle differs, so a deploy reaches every phone on its own.
export const loadedBundle = () => {
  const script = [...document.querySelectorAll("script[src]")].find((item) =>
    /\/main\.[a-z0-9]+\.js/.test(item.src)
  );
  return script ? script.src.split("/").pop() : null;
};

export const publishedBundle = (html) =>
  String(html ?? "").match(/main\.[a-z0-9]+\.js/)?.[0] ?? null;

const RETRY_WINDOW_MS = 2 * 60 * 1000;
const RELOAD_KEY = "bombila.versionReloadAt";

export const decideUpdate = ({
  current,
  next,
  lastAttemptAt = 0,
  now = Date.now(),
  retryWindowMs = RETRY_WINDOW_MS,
}) => {
  if (!next || next === current) return "current";
  if (lastAttemptAt && now - lastAttemptAt < retryWindowMs) return "stale";
  return "reload";
};

const useBundleRefresh = () => {
  const [status, setStatus] = useState("current");
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (checking.current || document.visibilityState !== "visible") return;
    const current = loadedBundle();
    if (!current) return;

    checking.current = true;
    try {
      const res = await fetch(`/index.html?ts=${Date.now()}`, {
        cache: "no-store",
        credentials: "omit",
        headers: { Accept: "text/html" },
      });
      if (!res.ok) return;
      const next = publishedBundle(await res.text());
      const lastAttemptAt = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      const decision = decideUpdate({ current, next, lastAttemptAt });

      if (decision === "current") {
        sessionStorage.removeItem(RELOAD_KEY);
        setStatus("current");
        return;
      }
      if (decision === "stale") {
        setStatus("stale");
        return;
      }

      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      setStatus("updating");
      const registration = await navigator.serviceWorker?.getRegistration?.();
      await Promise.race([
        registration?.update?.() ?? Promise.resolve(),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]).catch(() => undefined);
      window.location.reload();
    } catch (error) {
      // Offline or a flaky network: keep the bundle that already works
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    if (!loadedBundle()) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const timer = setInterval(check, 60 * 1000);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    check();
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  const refresh = useCallback(() => {
    sessionStorage.removeItem(RELOAD_KEY);
    setStatus("updating");
    try {
      navigator.serviceWorker?.getRegistration?.().then((registration) => registration?.update?.());
    } catch (error) {
      // reload still gets the fresh shell
    }
    window.location.reload();
  }, []);

  return { status, refresh };
};

export default useBundleRefresh;
